import React, {ReactElement} from "react";

import {
  BOND_VALUE_OPTIONS,
  COUNTRY_OPTIONS,
  FREIGHT_FORWARDERS_OPTIONS,
  REGIONS_OPTIONS,
  STATE_OPTIONS,
} from "/src/constants/form-options";
import {BOOLEAN_OPTIONS} from "../form-conditions/field-conditions/conditional-field-registry";

import Input from "../input/Input";
import Phone from "../phone/Phone";
import LegacyPhone from "../phone/LegacyPhone";
import SingleSelect from "../single-select/SingleSelect";
import MultiSelect from "../multi-select/MultiSelect";
import Checkbox from "../checkbox/Checkbox";
import Dropdown from "../dropdown/Dropdown";
import Textarea from "../textarea/Textarea";
import AddressAutoComplete from "../address/address-auto-complete/AddressAutoComplete";
import DatePicker from "../date-picker/DatePicker";
import MailingPhysicalAddress from "../address/mailing-physical-address/MailingPhysicalAddress";
import InfoMessage from "../info-message/InfoMessage";
import {AddressObjectType, InputStatus, InputType} from "/src/types";

import {INPUT_TYPES} from "/src/constants/form";
import {CheckboxProps} from "../checkbox/Checkbox";
import {containsEMEA} from "/src/utils/geolocate/geolocateUtils";

interface SimpleFormFieldProps {
  field:
    | Queries.FormFieldFragmentFragment
    | Queries.FormOptionsFragmentFragment;
  status: InputStatus;
  darkMode: boolean;
  disabled: boolean;
  readOnly: boolean;
  onChange: (change: string | AddressObjectType) => unknown;
  value?: string | Date | number;
  transparentBg: boolean;
  isEU: boolean;
  isEMEA: boolean;
  updateConditionState?: (str1: string, str2: string) => unknown;
  isInvisible?: (str: string) => boolean;
  valueForAddressField?: AddressObjectType;
  useLegacyPhoneInput?: boolean;
}

const SimpleFormField = (props: SimpleFormFieldProps): ReactElement | null => {
  const {
    field,
    status,
    darkMode = false,
    disabled = false,
    readOnly = false,
    onChange,
    value,
    transparentBg = false,
    isEU = false,
    isEMEA = false,
    updateConditionState,
    isInvisible,
    valueForAddressField,
    useLegacyPhoneInput = false,
  } = props;

  const {
    __typename,
    required,
    restrictWidth,
    // eslint-disable-next-line camelcase
    field_name,
    errorMessage,
    label,
    placeholder,
    checkForGdprCompliance = false,
    onlyShowForRegion = [],
  } = field;

  // if check for gdpr compliance is true, and isEU is false
  // do not render field
  if (checkForGdprCompliance === true && isEU === false) {
    return null;
  }

  // check for when field is set to only show in EMEA
  // do not render field
  if (!isEMEA && containsEMEA(onlyShowForRegion as [])) {
    return null;
  }

  // eslint-disable-next-line camelcase
  const fieldName = field_name || "";
  const fieldLabel = label || "";
  const fieldErrorMessage = errorMessage || "";
  const fieldPlaceholder = placeholder || "";

  // check if the current field is a child field with unmet condition
  const invisible = (isInvisible && isInvisible(fieldName)) || false;
  if (invisible) {
    return null;
  }

  const isRequired = invisible ? false : required === true;
  const hasRestrictedWidth = restrictWidth === true;

  switch (__typename) {
    case "ContentfulFormField": {
      let type =
        field.type != null
          ? INPUT_TYPES[field.type as keyof typeof INPUT_TYPES]
          : INPUT_TYPES.text;

      if (type == null) {
        type = INPUT_TYPES.text;
      }

      const maxLength = field.maxLength != null ? field.maxLength : 255;
      const minValue = field.minValue != null ? field.minValue : 0;
      const maxValue = field.maxValue != null ? field.maxValue : 255;
      const checked = field.checked != null ? field.checked === true : false;
      const fieldRichLabel =
        field.richTextLabel != null
          ? field.richTextLabel.richTextLabel || ""
          : "";

      switch (type) {
        case "date":
          return (
            <DatePicker
              transparentBg={transparentBg}
              status={status}
              errorMessage={fieldErrorMessage}
              darkMode={darkMode}
              value={
                value != null && value !== "" ? new Date(value) : new Date()
              }
              label={fieldLabel}
              required={isRequired}
              name={fieldName}
              onChange={onChange}
              key={fieldName}
              format="yyyy / MM / dd"
              valueFormat="YYYY-MM-DD"
            />
          );
        case "tel":
          return useLegacyPhoneInput === true ? (
            <LegacyPhone
              darkMode={darkMode}
              disabled={disabled}
              errorMessage={fieldErrorMessage}
              label={fieldLabel}
              name={fieldName}
              onChange={onChange}
              placeholder={fieldPlaceholder}
              required={isRequired}
              status={status}
              value={value as string}
            />
          ) : (
            <Phone
              label={fieldLabel}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              value={value as string}
              transparentBg={transparentBg}
            />
          );
        case "checkbox": {
          const {showCheckboxWithPillStyle} = field;
          return (
            <Checkbox
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange as any}
              isPill={showCheckboxWithPillStyle === true}
              label={fieldRichLabel || fieldLabel}
              checked={value !== "" || checked}
            />
          );
        }
        case "country": {
          const countries = COUNTRY_OPTIONS.map(country => {
            return {
              label: country.label,
              value: country.value,
            };
          });

          return (
            <Dropdown
              label={fieldLabel}
              options={countries}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              transparentBg={transparentBg}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={(val: string) => {
                if (onChange != null) {
                  onChange(val);
                }

                if (updateConditionState != null) {
                  updateConditionState(fieldName, val);
                }
              }}
              value={value as string}
              restrictWidth={hasRestrictedWidth}
            />
          );
        }
        case "state":
        case "regions":
        case "freightForwarders":
        case "bondValue": {
          const options = (t => {
            switch (t) {
              case "state":
                return STATE_OPTIONS;
              case "regions":
                return REGIONS_OPTIONS;
              case "freightForwarders":
                return FREIGHT_FORWARDERS_OPTIONS;
              case "bondValue":
                return BOND_VALUE_OPTIONS;
              default:
                return [];
            }
          })(type);

          const dropdownOptions = options.map(option => {
            return {
              label: option.label,
              value: option.label,
            };
          });

          return (
            <Dropdown
              label={fieldLabel}
              options={dropdownOptions}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              transparentBg={transparentBg}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              value={value as string}
              restrictWidth={hasRestrictedWidth}
            />
          );
        }
        case "textarea":
          return (
            <Textarea
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              value={value as string}
              label={fieldLabel}
              maxLength={maxLength}
              placeholder={fieldPlaceholder}
              transparentBg={transparentBg}
            />
          );
        case "mailingPhysicalAddress":
          return (
            <MailingPhysicalAddress
              label={fieldLabel}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              transparentBg={transparentBg}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              valueForAddressField={valueForAddressField}
            />
          );
        case "address":
          return (
            <AddressAutoComplete
              label={fieldLabel}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              transparentBg={transparentBg}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              valueForAddressField={valueForAddressField}
              addressType="mailing"
              sameAsMailing
            />
          );
        case "boolean": {
          const labelForTrueValue =
            field.labelForTrueValue != null ? field.labelForTrueValue : "Yes";
          const labelForFalseValue =
            field.labelForFalseValue != null ? field.labelForFalseValue : "No";

          const singleSelectOptions = [
            {label: labelForTrueValue, value: BOOLEAN_OPTIONS.TRUE_VALUE},
            {label: labelForFalseValue, value: BOOLEAN_OPTIONS.FALSE_VALUE},
          ];

          return (
            <SingleSelect
              label={fieldLabel}
              options={singleSelectOptions}
              required={isRequired}
              name={fieldName}
              key={fieldName}
              value={value as any}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              transparentBg={transparentBg}
              updateConditionState={updateConditionState}
            />
          );
        }
        case "info": {
          return (
            <InfoMessage
              message={fieldRichLabel || fieldLabel}
              visible={true}
              darkMode={darkMode}
              key={fieldName}
            />
          );
        }
        default:
          return (
            <Input
              required={isRequired}
              name={fieldName}
              key={fieldName}
              type={type as InputType}
              errorMessage={fieldErrorMessage}
              status={status}
              darkMode={darkMode}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={fieldPlaceholder}
              onChange={onChange}
              value={value as any}
              label={fieldLabel}
              maxLength={maxLength}
              min={minValue}
              max={maxValue}
              transparentBg={transparentBg}
            />
          );
      }
    }
    case "ContentfulFormOptions": {
      const dropdown = field.dropdown != null ? field.dropdown === true : false;
      const radio = field.radio != null ? field.radio === true : false;
      const options = field.options != null ? field.options : [];
      if (!options || !options.length) {
        return null;
      }

      if (dropdown === true) {
        // let's massage the options array data
        const dropdownOptions = options
          ? options.map(option => {
              return {
                label: option?.label || "",
                value: option?.value || "",
              };
            })
          : [];

        return (
          <Dropdown
            label={fieldLabel}
            options={dropdownOptions}
            required={isRequired}
            name={fieldName}
            key={fieldName}
            value={value as any}
            errorMessage={fieldErrorMessage}
            status={status}
            darkMode={darkMode}
            placeholder={fieldPlaceholder}
            disabled={disabled}
            readOnly={readOnly}
            onChange={(val: string) => {
              if (onChange != null) {
                onChange(val);
              }

              if (updateConditionState != null) {
                updateConditionState(fieldName, val);
              }
            }}
            transparentBg={transparentBg}
            restrictWidth={hasRestrictedWidth}
          />
        );
      }

      if (radio === true) {
        const singleSelectOptions = options
          ? options.map(option => {
              return {
                label: option?.label || "",
                value: option?.value || "",
              };
            })
          : [];

        return (
          <SingleSelect
            label={fieldLabel}
            options={singleSelectOptions}
            required={isRequired}
            name={fieldName}
            key={fieldName}
            value={value as any}
            errorMessage={fieldErrorMessage}
            status={status}
            darkMode={darkMode}
            disabled={disabled}
            readOnly={readOnly}
            onChange={onChange}
            transparentBg={transparentBg}
          />
        );
      }

      const multiSelectOptions: Array<CheckboxProps> = options
        ? options.map(option => {
            return {
              name: option?.field_name!,
              errorMessage: option?.errorMessage!,
              darkMode,
              disabled,
              readOnly,
              value: option?.value!,
              label: option?.label!,
              checked: false,
              onChange: () => {},
            };
          })
        : [];

      return (
        <MultiSelect
          options={multiSelectOptions}
          required={isRequired}
          name={fieldName}
          label={fieldLabel}
          key={fieldName}
          value={value as any}
          errorMessage={fieldErrorMessage}
          status={status}
          darkMode={darkMode}
          transparentBg={transparentBg}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
        />
      );
    }
    default:
      return null;
  }
};

export default SimpleFormField;
